import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  DialogContentText,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add,
  Delete,
  Warning,
  Edit,
  Visibility,
  Article,
  Videocam,
  MenuBook,
  Public,
  Lock,
  FavoriteBorder,
  BookmarkBorder,
  TrendingUp,
  ListAlt,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import postsService from '../../../services/PostsService';
import PostForm from './PostForm';

const BRAND = '#4DABA4';
const BRAND_DARK = '#2f6f6a';
const TRENDING_LIMIT = 5;

const metricValue = (row, key) => {
  if (!row) return 0;
  if (key === 'views') return Number(row.views ?? row.Views ?? 0) || 0;
  if (key === 'likes') return Number(row.likes ?? row.Likes ?? 0) || 0;
  if (key === 'saved') {
    return Number(row.savedCount ?? row.SavedCount ?? row.collectionCount ?? 0) || 0;
  }
  if (key === 'score') {
    const views = metricValue(row, 'views');
    const likes = metricValue(row, 'likes');
    const saved = metricValue(row, 'saved');
    return Number(row.engagementScore ?? row.EngagementScore ?? views + likes + saved) || 0;
  }
  return 0;
};

const Publicaciones = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listFilter, setListFilter] = useState('all'); // all | trending
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });
  const [openDialog, setOpenDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [viewingData, setViewingData] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    postId: null,
    postTitle: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const showToast = (message, severity = 'error') => {
    setToast({ open: true, message, severity });
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await postsService.getPosts();
      setPosts((data || []).map((post) => postsService.normalizePostMetrics(post)));
    } catch (err) {
      showToast('Error al cargar las publicaciones');
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const trendingPosts = useMemo(() => {
    return [...posts]
      .sort((a, b) => {
        const scoreDiff = metricValue(b, 'score') - metricValue(a, 'score');
        if (scoreDiff !== 0) return scoreDiff;
        const savedDiff = metricValue(b, 'saved') - metricValue(a, 'saved');
        if (savedDiff !== 0) return savedDiff;
        const likesDiff = metricValue(b, 'likes') - metricValue(a, 'likes');
        if (likesDiff !== 0) return likesDiff;
        return metricValue(b, 'views') - metricValue(a, 'views');
      })
      .slice(0, TRENDING_LIMIT)
      .map((post, index) => ({ ...post, trendRank: index + 1 }));
  }, [posts]);

  const displayedPosts = useMemo(() => {
    if (listFilter === 'trending') return trendingPosts;
    return [...posts].sort((a, b) => {
      const aTime = new Date(a.createdAt || a.CreatedAt || 0).getTime() || 0;
      const bTime = new Date(b.createdAt || b.CreatedAt || 0).getTime() || 0;
      if (bTime !== aTime) return bTime - aTime;
      return Number(b.id || 0) - Number(a.id || 0);
    });
  }, [listFilter, posts, trendingPosts]);

  const trendingIds = useMemo(
    () => new Set(trendingPosts.map((p) => p.id)),
    [trendingPosts]
  );

  const handleOpenDialog = () => {
    setEditingData(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingData(null);
  };

  const handleEdit = async (row) => {
    try {
      const postData = await postsService.getPostById(row.id);
      setEditingData(postsService.normalizePostMetrics(postData));
      setOpenDialog(true);
    } catch (err) {
      console.error('Error al obtener el post:', err);
      showToast('Error al cargar el post para editar');
    }
  };

  const handleView = async (row) => {
    try {
      const postData = await postsService.getPostById(row.id);
      const normalized = postsService.normalizePostMetrics(postData);
      const rank = trendingPosts.findIndex((p) => p.id === normalized.id);
      setViewingData({
        ...normalized,
        trendRank: rank >= 0 ? rank + 1 : null,
      });
      setViewDialog(true);
    } catch (err) {
      console.error('Error al obtener el post:', err);
      showToast('Error al cargar el post');
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialog(false);
    setViewingData(null);
  };

  const handleDeleteClick = (row) => {
    setDeleteConfirmDialog({
      open: true,
      postId: row.id,
      postTitle: row.title || 'esta publicación',
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog({
      open: false,
      postId: null,
      postTitle: '',
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmDialog.postId) return;
    try {
      setIsDeleting(true);
      await postsService.deletePost(deleteConfirmDialog.postId);
      await fetchPosts();
      handleCloseDeleteDialog();
      showToast('Publicación eliminada', 'success');
    } catch (err) {
      console.error('Error eliminando post:', err);
      showToast('Error al eliminar la publicación');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    handleCloseDialog();
    fetchPosts();
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Publicación',
      flex: 1.4,
      minWidth: 240,
      renderCell: (params) => {
        const isTop = trendingIds.has(params.row.id);
        const rank = params.row.trendRank
          || trendingPosts.findIndex((p) => p.id === params.row.id) + 1
          || null;
        return (
          <Box className="flex min-w-0 items-center gap-3 py-1">
            <Box className="relative h-11 w-11 flex-shrink-0 overflow-hidden rounded-lg bg-slate-100">
              {params.row.thumbnail ? (
                <img
                  src={postsService.resolveMediaUrl(params.row.thumbnail) || params.row.thumbnail}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <Box
                className="h-full w-full items-center justify-center text-slate-400"
                style={{ display: params.row.thumbnail ? 'none' : 'flex' }}
              >
                <Article fontSize="small" />
              </Box>
              {listFilter === 'trending' && rank > 0 && (
                <Box
                  className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
                  sx={{ bgcolor: BRAND }}
                >
                  {rank}
                </Box>
              )}
            </Box>
            <Box className="min-w-0">
              <Box className="flex items-center gap-1.5">
                <Typography className="truncate text-sm font-semibold text-slate-800">
                  {params.value || 'Sin título'}
                </Typography>
                {isTop && listFilter === 'all' && (
                  <Tooltip title={`Top ${rank} en tendencia`}>
                    <TrendingUp sx={{ fontSize: 16, color: BRAND }} />
                  </Tooltip>
                )}
              </Box>
              <Typography className="truncate text-xs text-slate-500">
                ID #{params.row.id}
                {params.row.teacherName ? ` · ${params.row.teacherName}` : ''}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'category',
      headerName: 'Tipo',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          icon={params.value === 'Video' ? <Videocam /> : <MenuBook />}
          label={params.value || 'N/A'}
          sx={{
            bgcolor: 'rgba(77,171,164,0.12)',
            color: BRAND_DARK,
            fontWeight: 700,
            '& .MuiChip-icon': { color: BRAND },
          }}
        />
      ),
    },
    {
      field: 'isPublic',
      headerName: 'Visibilidad',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          icon={params.value ? <Public /> : <Lock />}
          label={params.value ? 'Pública' : 'Privada'}
          sx={{
            bgcolor: params.value ? 'rgba(136,178,79,0.18)' : '#f1f5f9',
            color: params.value ? '#4d7c0f' : '#64748b',
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'views',
      headerName: 'Vistas',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_, row) => metricValue(row, 'views'),
      renderCell: (params) => (
        <Tooltip title="Visualizaciones">
          <Box className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700">
            <Visibility sx={{ fontSize: 16, color: BRAND }} />
            {metricValue(params.row, 'views')}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'likes',
      headerName: 'Likes',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_, row) => metricValue(row, 'likes'),
      renderCell: (params) => (
        <Tooltip title="Me gusta">
          <Box className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700">
            <FavoriteBorder sx={{ fontSize: 16, color: '#e11d48' }} />
            {metricValue(params.row, 'likes')}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'savedCount',
      headerName: 'Guardados',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_, row) => metricValue(row, 'saved'),
      renderCell: (params) => (
        <Tooltip title="Veces guardada por usuarios">
          <Box className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700">
            <BookmarkBorder sx={{ fontSize: 16, color: '#ca8a04' }} />
            {metricValue(params.row, 'saved')}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'engagementScore',
      headerName: 'Score',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_, row) => metricValue(row, 'score'),
      renderCell: (params) => (
        <Tooltip title="Tendencia = vistas + likes + guardados">
          <Chip
            size="small"
            label={metricValue(params.row, 'score')}
            sx={{ bgcolor: 'rgba(77,171,164,0.15)', color: BRAND_DARK, fontWeight: 700 }}
          />
        </Tooltip>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Creado',
      width: 110,
      valueGetter: (_, row) => {
        const raw = row?.createdAt || row?.CreatedAt;
        const t = raw ? new Date(raw).getTime() : 0;
        return Number.isFinite(t) ? t : 0;
      },
      sortComparator: (a, b) => Number(a || 0) - Number(b || 0),
      renderCell: (params) => {
        const raw = params.row?.createdAt || params.row?.CreatedAt;
        return raw ? new Date(raw).toLocaleDateString('es-ES') : 'N/A';
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Ver métricas">
            <IconButton size="small" onClick={() => handleView(params.row)} sx={{ color: BRAND }}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row)}
              sx={{ color: BRAND_DARK, bgcolor: 'rgba(77,171,164,0.12)' }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" onClick={() => handleDeleteClick(params.row)} sx={{ color: '#dc2626' }}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (loading) {
    return (
      <Box className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <CircularProgress sx={{ color: BRAND }} />
        <Typography color="text.secondary">Cargando publicaciones...</Typography>
      </Box>
    );
  }

  return (
    <Box className="space-y-5">
      <Box className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <Box className="flex items-center gap-3">
          <Box
            className="flex h-11 w-11 items-center justify-center rounded-xl text-white"
            sx={{ bgcolor: BRAND }}
          >
            <Article />
          </Box>
          <Box>
            <Typography variant="h5" className="font-bold text-slate-800">
              Publicaciones
            </Typography>
            <Typography className="text-sm text-slate-500">
              Métricas del feed social · {posts.length} en total
              {listFilter === 'trending' ? ` · Top ${TRENDING_LIMIT} tendencia` : ''}
            </Typography>
          </Box>
        </Box>

        <Box className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={listFilter}
            onChange={(_, value) => value && setListFilter(value)}
            sx={{
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 700,
                border: 'none',
                px: 1.75,
                borderRadius: '10px !important',
                color: '#64748b',
                '&.Mui-selected': {
                  bgcolor: BRAND,
                  color: '#fff',
                  '&:hover': { bgcolor: BRAND_DARK },
                },
              },
            }}
          >
            <ToggleButton value="all">
              <ListAlt sx={{ fontSize: 18, mr: 0.75 }} />
              Todas
            </ToggleButton>
            <ToggleButton value="trending">
              <TrendingUp sx={{ fontSize: 18, mr: 0.75 }} />
              Tendencia (Top {TRENDING_LIMIT})
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            onClick={handleOpenDialog}
            startIcon={<Add />}
            variant="contained"
            sx={{
              bgcolor: BRAND,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '12px',
              px: 2.5,
              py: 1.2,
              boxShadow: '0 8px 20px rgba(77,171,164,0.28)',
              '&:hover': { bgcolor: BRAND_DARK },
            }}
          >
            Nueva publicación
          </Button>
        </Box>
      </Box>

      {listFilter === 'trending' && (
        <Box className="rounded-2xl border border-[#4DABA4]/25 bg-[#4DABA4]/5 px-4 py-3 text-sm text-slate-700">
          <strong>Criterio de tendencia:</strong> score = visualizaciones + likes + veces guardada.
          Se muestran las {TRENDING_LIMIT} publicaciones con mayor engagement.
        </Box>
      )}

      <Box className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="h-[min(70vh,640px)]">
          <DataGrid
            rows={displayedPosts}
            columns={columns}
            pageSizeOptions={[5, 10, 20]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: {
                sortModel: [{ field: 'createdAt', sort: 'desc' }],
              },
            }}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            getRowHeight={() => 68}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: BRAND,
                color: '#fff',
                fontWeight: 700,
                borderBottom: 'none',
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: '#fff',
                  fontWeight: 700,
                },
                '& .MuiIconButton-root': { color: '#fff' },
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(77,171,164,0.06)',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: `1px solid ${BRAND}33`,
                backgroundColor: '#f8fafc',
              },
            }}
          />
        </div>
      </Box>

      {/* Crear / editar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '18px', overflow: 'hidden', maxHeight: '94vh' },
        }}
      >
        <DialogTitle
          className="flex items-center gap-2"
          sx={{ bgcolor: BRAND, color: '#fff', py: 2 }}
        >
          <Article />
          {editingData ? 'Editar publicación' : 'Nueva publicación'}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc', p: { xs: 2, sm: 3 } }}>
          <PostForm
            initialData={editingData}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseDialog}
            hideTitle
          />
        </DialogContent>
      </Dialog>

      {/* Ver */}
      <Dialog
        open={viewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ bgcolor: BRAND, color: '#fff' }} className="flex items-center gap-2">
          <Visibility />
          Vista de publicación
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
          {viewingData && (
            <Box className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              {viewingData.thumbnail && (
                <img
                  src={postsService.resolveMediaUrl(viewingData.thumbnail) || viewingData.thumbnail}
                  alt={viewingData.title}
                  className="h-52 w-full object-cover bg-slate-100"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/img/default.png';
                  }}
                />
              )}
              <Box className="space-y-3 p-4">
                <Box className="flex flex-wrap gap-1.5">
                  <Chip
                    size="small"
                    icon={viewingData.category === 'Video' ? <Videocam /> : <MenuBook />}
                    label={viewingData.category || 'N/A'}
                    sx={{ bgcolor: 'rgba(77,171,164,0.15)', color: BRAND_DARK, fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    icon={viewingData.isPublic ? <Public /> : <Lock />}
                    label={viewingData.isPublic ? 'Pública' : 'Privada'}
                    sx={{
                      bgcolor: viewingData.isPublic ? 'rgba(136,178,79,0.18)' : '#f1f5f9',
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Typography variant="h6" className="font-bold text-slate-800">
                  {viewingData.title}
                </Typography>
                <Typography className="whitespace-pre-wrap text-sm text-slate-600">
                  {viewingData.description}
                </Typography>
                <Box className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Box className="rounded-xl bg-slate-50 px-3 py-2 text-center">
                    <Visibility sx={{ fontSize: 18, color: BRAND }} />
                    <Typography className="text-lg font-bold text-slate-800">
                      {metricValue(viewingData, 'views')}
                    </Typography>
                    <Typography className="text-[11px] text-slate-500">Vistas</Typography>
                  </Box>
                  <Box className="rounded-xl bg-slate-50 px-3 py-2 text-center">
                    <FavoriteBorder sx={{ fontSize: 18, color: '#e11d48' }} />
                    <Typography className="text-lg font-bold text-slate-800">
                      {metricValue(viewingData, 'likes')}
                    </Typography>
                    <Typography className="text-[11px] text-slate-500">Likes</Typography>
                  </Box>
                  <Box className="rounded-xl bg-slate-50 px-3 py-2 text-center">
                    <BookmarkBorder sx={{ fontSize: 18, color: '#ca8a04' }} />
                    <Typography className="text-lg font-bold text-slate-800">
                      {metricValue(viewingData, 'saved')}
                    </Typography>
                    <Typography className="text-[11px] text-slate-500">Guardados</Typography>
                  </Box>
                  <Box className="rounded-xl bg-[#4DABA4]/10 px-3 py-2 text-center">
                    <TrendingUp sx={{ fontSize: 18, color: BRAND }} />
                    <Typography className="text-lg font-bold text-slate-800">
                      {metricValue(viewingData, 'score')}
                    </Typography>
                    <Typography className="text-[11px] text-slate-500">
                      {viewingData.trendRank ? `Top #${viewingData.trendRank}` : 'Score'}
                    </Typography>
                  </Box>
                </Box>
                <Typography className="text-xs text-slate-500">
                  Profesor: {viewingData.teacherName || 'N/A'}
                </Typography>
                {viewingData.labels?.length > 0 && (
                  <Box className="flex flex-wrap gap-1">
                    {viewingData.labels.map((label, idx) => (
                      <Chip
                        key={idx}
                        label={label.name}
                        size="small"
                        sx={{ bgcolor: 'rgba(77,171,164,0.1)', color: BRAND_DARK }}
                      />
                    ))}
                  </Box>
                )}
                {(viewingData.videoUrl || viewingData.fileUrl) && (
                  <Button
                    href={viewingData.videoUrl || viewingData.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      borderColor: BRAND,
                      color: BRAND_DARK,
                      borderRadius: '10px',
                    }}
                  >
                    Abrir material vinculado
                  </Button>
                )}
                <Typography className="text-xs text-slate-400">
                  Creado:{' '}
                  {viewingData.createdAt
                    ? new Date(viewingData.createdAt).toLocaleString('es-ES')
                    : 'N/A'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {viewingData && (
            <Button
              onClick={() => {
                handleCloseViewDialog();
                handleEdit(viewingData);
              }}
              startIcon={<Edit />}
              sx={{ textTransform: 'none', color: BRAND_DARK, fontWeight: 700 }}
            >
              Editar
            </Button>
          )}
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              textTransform: 'none',
              bgcolor: BRAND,
              borderRadius: '10px',
              fontWeight: 700,
              '&:hover': { bgcolor: BRAND_DARK },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Eliminar */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle className="flex items-center gap-2 border-b border-red-100 bg-red-50 text-red-600">
          <Warning />
          Confirmar eliminación
        </DialogTitle>
        <DialogContent className="py-5">
          <DialogContentText className="text-slate-700">
            ¿Eliminar la publicación{' '}
            <strong className="text-red-600">"{deleteConfirmDialog.postTitle}"</strong>? Esta acción
            no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="gap-2 p-4">
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={isDeleting}
            sx={{ textTransform: 'none', borderRadius: '10px' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 700 }}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Publicaciones;
